import { Button } from 'primereact/button'
import { ToggleButton } from 'primereact/togglebutton'
import { Tooltip } from 'primereact/tooltip'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../redux/hooks'
import { MokaiRoute, SidebarRoutes_Authorized } from '../../router/routes'
import { openDokai } from '../../services/help/openDokai'
import { hasAnyRequiredClaim } from '../../util/validation/hasClaim'
import { LanguageSelect } from '../util/button/language-select/LanguageSelect'

export const SidebarMenu = (): JSX.Element => {
  // this is required to be able to change the language in the navigation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t } = useTranslation()

  const userClaims = useAppSelector((state) => state.claims)

  const [menuItems, setMenuItems] = useState<any[]>([])
  const [validRoutes, setValidRoutes] = useState<MokaiRoute[]>([])

  const navigate = useNavigate()

  const setRoute = (route: string) => {
    navigate(route)
    const activeItem = menuItems.find((item) => item.path === route)
    activeItem.className = 'active-menu-item'
  }

  const getNavSetting = () => {
    const item = window.localStorage.getItem('mokai-navigation-expanded')
    if (!item) {
      return '1'
    }
    return item
  }

  const [navState, setNavState] = useState<string>(getNavSetting())

  const expandNav = () => {
    window.localStorage.setItem('mokai-navigation-expanded', '0')
    setNavState('0')
  }
  const shrinkNav = () => {
    window.localStorage.setItem('mokai-navigation-expanded', '1')
    setNavState('1')
  }

  const toggleNav = () => {
    const navValue = getNavSetting()
    if (navValue === '1') {
      expandNav()
    } else {
      shrinkNav()
    }
  }

  useEffect(() => {
    const items: any[] = []
    const vRoutes: MokaiRoute[] = []
    SidebarRoutes_Authorized.forEach((route: MokaiRoute) => {
      let requiredRoutes: string[] = [route.mainModule]
      if (route?.subModules) {
        requiredRoutes = requiredRoutes.concat(route.subModules)
      }
      const userHasClaim = hasAnyRequiredClaim(userClaims, requiredRoutes)
      if (userHasClaim) {
        vRoutes.push(route)
        items.push({
          label: t<string>('navigation.' + route.name),
          icon: route.icon,
          command: () => setRoute(route.path),
        })
      }
    })
    setValidRoutes(vRoutes)
    setMenuItems(items)
  }, [userClaims.length])

  const location = useLocation()

  return (
    <div
      className={`sidebar-menu ${
        navState === '1' ? 'expanded-nav' : 'shrunk-nav'
      }`}
      role={'menubar'}
    >
      <ToggleButton
        checked={navState === '0'}
        offIcon={'fa-solid fa-chevron-left'}
        onIcon={'fa-solid fa-chevron-right'}
        onLabel={''}
        tooltip={
          navState === '0'
            ? t<string>('core.expand')
            : t<string>('core.collapse')
        }
        tooltipOptions={{ showDelay: 800, className: 'blue-tooltip' }}
        offLabel={''}
        className={
          'p-button-rounded p-button-text nav-toggle-button p-button-raised mb-1'
        }
        onChange={() => toggleNav()}
      />
      {validRoutes.map((route: MokaiRoute) => {
        return (
          <NavLink
            className={'link-item ' + route.name}
            key={route.name}
            to={route.path}
          >
            <div
              className={
                'nav-icon-wrapper justify-content-center align-items-center'
              }
            >
              <span className={route.icon + ' mr-2 nav-icon'} />
            </div>
            <span
              className={navState === '1' ? 'opacity-fade' : 'no-opacity-fade'}
            >
              {t<string>('navigation.' + route.name)}
            </span>
            {navState !== '1' && (
              <Tooltip
                target={`.${route.name}`}
                content={t<string>('navigation.' + route.name)}
                position={'right'}
                showDelay={800}
                className={'blue-tooltip'}
              />
            )}
          </NavLink>
        )
      })}
      <div
        className={'w-full flex mt-auto align-items-center justify-content-end'}
      >
        <LanguageSelect />
      </div>
      <Button
        icon={'fas fa-question'}
        onClick={() => openDokai(location)}
        className={
          '' +
          'p-button-rounded ' +
          'p-button-text ' +
          'p-button-outlined ' +
          'p-button-raised help-icon ml-auto mb-2'
        }
        tooltip={t<string>('core.open_dokai')}
        tooltipOptions={{
          className: 'blue-tooltip',
          showDelay: 800,
          position: 'left',
        }}
      />
    </div>
  )
}